import type { PaginationType } from '../types/Pagination.types';
import type { GameDto, GameLaunchUrlPayload, GameLaunchUrlResponse, GameListItemDto, GamesFiltersParamsPayload, RoundsWithWinsParams, RoundWithWinDto, SharedRoundDto, ShareTokenDto } from './games.schema';
import { validateWithZod } from '@/utils/zod.utils';
import { ENDPOINTS } from '../endpoints';
import { httpClient } from '../httpClient';
import { PaginationSchema } from '../types/Pagination.types';
import { GameDtoSchema, GameLaunchUrlPayloadSchema, GameLaunchUrlResponseSchema, GameListItemDtoSchema, GamesParamsSchema, RoundsWithWinsParamsSchema, RoundWithWinDtoSchema, SharedRoundDtoSchema, ShareTokenDtoSchema } from './games.schema';

export const gamesApi = {
  /**
   * @deprecated This method is deprecated. Use the new games query method instead.
   */
  async getAll(params: GamesFiltersParamsPayload) {
    const validatedParams = validateWithZod(GamesParamsSchema, params, 'frontend');

    const response = await httpClient.get<PaginationType<GameListItemDto>>(
      ENDPOINTS.games.all,
      { params: validatedParams, skipAuth: true },
    );

    return validateWithZod(PaginationSchema(GameListItemDtoSchema), response, 'backend');
  },

  async fetchAll(params?: GamesFiltersParamsPayload) {
    const validatedParams = params ? validateWithZod(GamesParamsSchema, params, 'frontend') : undefined;

    const response = await httpClient.get<PaginationType<GameListItemDto>>(
      ENDPOINTS.games.all,
      { params: validatedParams, skipAuth: true },
    );

    return validateWithZod(PaginationSchema(GameListItemDtoSchema), response);
  },

  /**
   * @deprecated This method is deprecated. Use the new games query method instead.
   */
  async getById(id: string) {
    const response = await httpClient.get<GameDto>(ENDPOINTS.games.byId(id));

    return validateWithZod(GameDtoSchema, response, 'backend');
  },

  async fetchById(id: string) {
    const response = await httpClient.get<GameDto>(ENDPOINTS.games.byId(id));

    return validateWithZod(GameDtoSchema, response, 'backend');
  },

  async getLaunchUrl(payload: GameLaunchUrlPayload) {
    const validatedPayload = validateWithZod(GameLaunchUrlPayloadSchema, payload, 'frontend');

    const response = await httpClient.post<GameLaunchUrlResponse>(ENDPOINTS.games.launchUrl, validatedPayload);

    return validateWithZod(GameLaunchUrlResponseSchema, response, 'backend');
  },

  async getListOfFavoritesGames(params?: GamesFiltersParamsPayload) {
    const validatedParams = params ? validateWithZod(GamesParamsSchema, params, 'frontend') : undefined;

    const response = await httpClient.get<PaginationType<GameListItemDto>>(
      ENDPOINTS.games.favorites,
      validatedParams ? { params: validatedParams } : undefined,
    );

    return validateWithZod(PaginationSchema(GameListItemDtoSchema), response, 'backend');
  },

  async likeGame(gameId: string) {
    const response = await httpClient.post<GameDto>(ENDPOINTS.games.likeGame(gameId));

    return response;
  },

  async dislikeGame(gameId: string) {
    const response = await httpClient.delete<GameDto>(ENDPOINTS.games.dislikeGame(gameId));

    return response;
  },

  /**
   * Get similar games by Id needs for linking similar games for seo
   */
  async getSimilarGamesById(id: string) {
    return await httpClient.get<GameDto[]>(ENDPOINTS.games.similarById(id), { skipAuth: true });
  },

  async getRoundsWithWins(gameId: string, params?: RoundsWithWinsParams) {
    const validatedParams = params ? validateWithZod(RoundsWithWinsParamsSchema, params, 'frontend') : undefined;

    const response = await httpClient.get<PaginationType<RoundWithWinDto>>(
      ENDPOINTS.games.roundsWithWins(gameId),
      validatedParams ? { params: validatedParams } : undefined,
    );

    return validateWithZod(PaginationSchema(RoundWithWinDtoSchema), response, 'backend');
  },

  async verifyRound(roundId: string) {
    return await httpClient.post<RoundWithWinDto>(
      ENDPOINTS.games.verifyRound(roundId),
    );
  },

  async createShareLink(gameId: string, roundId: string): Promise<ShareTokenDto> {
    const response = await httpClient.post<ShareTokenDto>(ENDPOINTS.games.shareRound(gameId, roundId));

    return validateWithZod(ShareTokenDtoSchema, response, 'backend');
  },

  async getSharedRound(token: string): Promise<SharedRoundDto> {
    const response = await httpClient.get<SharedRoundDto>(ENDPOINTS.games.sharedRound(token), { skipAuth: true });

    return validateWithZod(SharedRoundDtoSchema, response, 'backend');
  },
};
